import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Container } from "react-bootstrap";
import { getLMSEnrollments } from "../../Redux/Actions/enrollmentsAction";
import "bootstrap/dist/css/bootstrap.css";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import DoceboRosterTable from "../components/doceboRosterTable";
import "./../../config";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../components/CommonModal/manage-roster-modal.css";
import "./manage-course-container.css";
import ManageCourseContainer from "./manageCourseContainer";
import FindLearnerModal from "../components/FindLearnerModal";
import CommonModal from "../../components/CommonModal";

const CONFIRM_PREF_KEY = "rapidRoster:disableLearnerConfirmPrompt";

const buildLearnerKey = (user) =>
  `${user.user_id}:${user.first_name ?? ""} ${user.last_name ?? ""}`.trim();

const getUsersFromPeopleResponse = (data) => {
  if (!data) return [];
  if (Array.isArray(data.users) && data.users.length > 0) return data.users;
  if (Array.isArray(data.userInfo) && data.userInfo.length > 0) return data.userInfo;
  return [];
};

// DOCEBO
const ManageRoster = () => {
  const [term, setTerm] = useState("");

  const [enrollmentsLoading, setEnrollmentsLoading] = useState(true);

  const [courseId, setCourseId] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [eventId, setEventId] = useState("");

  const [courseName, setCourseName] = useState("");
  const [sessionName, setSessionName] = useState("");
  const [eventName, setEventName] = useState("");

  const [showFindLearner, setShowFindLearner] = useState(false);
  const [showAlert, setShowAlert] = useState({ userNotFound: false });
  const [pendingLearner, setPendingLearner] = useState(null);
  const [showConfirmLearner, setShowConfirmLearner] = useState(false);
  const [dontPromptConfirmations, setDontPromptConfirmations] = useState(() => {
    try {
      return sessionStorage.getItem(CONFIRM_PREF_KEY) === "true";
    } catch {
      return false;
    }
  });

  const baseApiUrl = global.config.baseApiUrl;
  const companyId = global.config.companyId;

  const {
    course: currentCourse,
    session: currentSession,
    event: currentEvent,
  } = useSelector((state) => state.courses);
  const { user: currentUser } = useSelector((state) => state.auth);

  const dispatch = useDispatch();

  const onChangeTerm = (e) => {
    setTerm(e.target.value);
  };

  const toggleAlertState = (key, bool) => {
    setShowAlert((prev) => ({ ...prev, [key]: bool }));
  };

  const hideAlert = (key) => toggleAlertState(key, false);
  const enableAlert = (key) => toggleAlertState(key, true);

  const applyMomentiveBadgeTransform = (value) => {
    if (!companyId.includes("momentive")) return value;

    const regExp = /[a-z]/i;
    if (regExp.test(value)) {
      const half = Math.floor(value.length / 2);
      return value.slice(0, half);
    }
    return value.slice(-5);
  };

  const lookupSingleLearner = async (rawTerm, scanned = "N") => {
    const trimmed = rawTerm?.trim();
    if (!trimmed) return null;

    const userId = applyMomentiveBadgeTransform(trimmed);

    const postPeople = (body) =>
      axios.post(`${baseApiUrl}/people`, {
        ...body,
        accessToken: currentUser.accessToken,
        scanned,
      });

    let res = await postPeople({ userId });
    let users = getUsersFromPeopleResponse(res.data);
    if (users.length === 1) return users[0];
    if (users.length > 1) return null;

    res = await postPeople({ search_text: trimmed });
    users = getUsersFromPeopleResponse(res.data);
    if (users.length === 1) return users[0];
    return null;
  };

  const submitLearnerCheckIn = async (learnerKey) => {
    if (!learnerKey) return;

    setEnrollmentsLoading(true);

    const course_id = currentCourse.course_id;
    const session_id = currentSession.session_id;
    const event_id = currentEvent.event_id;

    setCourseId(course_id);
    setSessionId(session_id);
    setEventId(event_id);
    setCourseName(currentCourse.course_name);
    setSessionName(currentSession.session_name);
    setEventName(currentEvent.event_name);

    const reqBody = {
      courseId: course_id,
      sessionId: session_id,
      eventId: event_id,
      accessToken: currentUser.accessToken,
      learners: [learnerKey],
    };

    try {
      const data = await axios.post(`${baseApiUrl}/learner/check`, reqBody);

      if (data?.data?.enrollments?.length > 0) {
        const parsedId = learnerKey.includes(":")
          ? learnerKey.substring(0, learnerKey.indexOf(":"))
          : learnerKey;
        const userObj = data.data.enrollments.find((x) => x.id === parsedId);
        if (userObj) {
          if (userObj.completed === "1") {
            toast.warn(`${userObj.name} has already been marked Complete.`, {
              position: toast.POSITION.BOTTOM_CENTER,
            });
          } else {
            toast.info(`${userObj.name} has been successfully Enrolled.`, {
              position: toast.POSITION.TOP_CENTER,
            });
          }
        }
      }

      dispatch(getLMSEnrollments(data, false));
    } catch (error) {
      console.error(error);
    } finally {
      setEnrollmentsLoading(false);
    }
  };

  const enrollUser = async (e) => {
    if (!term?.trim()) return;

    const scannerKeyCodes = [13, 9];
    const scanned = scannerKeyCodes.includes(e?.keyCode) ? "Y" : "N";

    try {
      const user = await lookupSingleLearner(term, scanned);
      if (!user) {
        enableAlert("userNotFound");
        return;
      }

      const learnerKey = buildLearnerKey(user);

      if (dontPromptConfirmations) {
        setTerm("");
        await submitLearnerCheckIn(learnerKey);
        return;
      }

      setPendingLearner({ user, learnerKey });
      setShowConfirmLearner(true);
    } catch (error) {
      console.error(error);
      enableAlert("userNotFound");
    }
  };

  const handleOpenFindLearner = () => {
    setShowFindLearner(true);
  };

  const handleFindLearnerClose = () => {
    setShowFindLearner(false);
  };

  const handleFindLearnerConfirm = async (learnerKey) => {
    if (!learnerKey) return;
    await submitLearnerCheckIn(learnerKey);
  };

  const handleCancelConfirmLearner = () => {
    setShowConfirmLearner(false);
    setPendingLearner(null);
  };

  const handleApproveConfirmLearner = async () => {
    if (!pendingLearner?.learnerKey) return;
    setShowConfirmLearner(false);
    setTerm("");

    try {
      sessionStorage.setItem(CONFIRM_PREF_KEY, String(dontPromptConfirmations));
    } catch {
      // ignore
    }

    await submitLearnerCheckIn(pendingLearner.learnerKey);
    setPendingLearner(null);
  };

  const confirmFields = [
    { label: "User Id", value: pendingLearner?.user?.user_id ?? "N/A" },
    { label: "Username", value: pendingLearner?.user?.username ?? "N/A" },
    { label: "Email", value: pendingLearner?.user?.email ?? "N/A" },
    {
      label: "Manager",
      value: pendingLearner?.user?.is_manager ? "Yes" : "No",
    },
  ];

  useEffect(() => {
    if (currentCourse) {
      setCourseId(currentCourse.course_id);
      setCourseName(currentCourse.course_name);
    }
  }, [currentCourse]);

  useEffect(() => {
    if (currentSession) {
      setSessionId(currentSession.session_id);
      setSessionName(currentSession.session_name);
    }
  }, [currentSession]);

  useEffect(() => {
    if (currentEvent) {
      setEventId(currentEvent.event_id);
      setEventName(currentEvent.event_name);
    }
  }, [currentEvent]);

  useEffect(() => {
    let unmounted = false;

    async function getDoceboEnrollments() {
      setEnrollmentsLoading(true);

      const url = `${baseApiUrl}/enrollments`;
      let course_id = "";
      let session_id = "";
      let event_id = "";

      let course_name = "";
      let session_name = "";
      let event_name = "";

      if (currentCourse) {
        course_id = currentCourse.course_id;
        session_id = currentSession.session_id;
        event_id = currentEvent.event_id;
        course_name = currentCourse.course_name;
        session_name = currentSession.session_name;
        event_name = currentEvent.event_name;
      }

      setCourseId(course_id);
      setSessionId(session_id);
      setEventId(event_id);
      setCourseName(course_name);
      setSessionName(session_name);
      setEventName(event_name);

      const reqBody = {
        courseId: course_id,
        sessionId: session_id,
        eventId: event_id,
        accessToken: currentUser.accessToken,
      };

      try {
        const data = await axios.post(url, reqBody);
        if (data && !unmounted) {
          dispatch(getLMSEnrollments(data, false));
        }
      } catch (error) {
        console.error(error);
      }

      if (!unmounted) {
        setEnrollmentsLoading(false);
      }
    }

    getDoceboEnrollments();

    return () => {
      unmounted = true;
    };
  }, []);

  const courseDetails = [
    { key: "Course", value: `${courseId} - ${courseName}` },
    { key: "Session", value: `${sessionId} - ${sessionName}` },
    { key: "Event", value: `${eventId} - ${eventName}` },
  ];

  const doceboSearchHint = (
    <>
      Scan Badge or Enter ID <br />
      (Employee ID, Username, or Badge ID)
    </>
  );

  return (
    <>
      <ManageCourseContainer
        pageTitle="Manage Roster"
        searchHint={doceboSearchHint}
        rosterContent={<DoceboRosterTable />}
        loadingState={{
          isLoading: enrollmentsLoading,
          setIsLoading: setEnrollmentsLoading,
          enrollmentsLoading,
        }}
        courseDetails={courseDetails}
        search={{
          term,
          onChangeTerm,
          onFindLearner: handleOpenFindLearner,
          onConfirm: enrollUser,
        }}
      />

      <FindLearnerModal
        show={showFindLearner}
        onClose={handleFindLearnerClose}
        onConfirm={handleFindLearnerConfirm}
        singleSearchField
        accessToken={currentUser.accessToken}
      />

      <CommonModal
        show={showConfirmLearner}
        onHide={handleCancelConfirmLearner}
        title={<Modal.Title>Confirm Learner</Modal.Title>}
        footer={
          <div className="d-flex justify-content-between align-items-center w-100 px-2">
            <Form.Check
              type="checkbox"
              id="dont-prompt-confirmations-docebo"
              label="Don't prompt for confirmations"
              className="field-label"
              checked={dontPromptConfirmations}
              onChange={(e) => setDontPromptConfirmations(e.target.checked)}
            />
            <div className="d-flex" style={{ gap: "10px" }}>
              <Button variant="dark" onClick={handleCancelConfirmLearner}>
                Cancel
              </Button>
              <Button variant="success" onClick={handleApproveConfirmLearner}>
                Approve
              </Button>
            </div>
          </div>
        }
        modalProps={{
          centered: true,
          backdrop: "static",
          className: "manage-roster-modal confirm-learner-modal",
          size: "lg",
        }}
      >
        <Container>
          <Row className="align-items-center">
            <Col xs="auto">
              <div
                style={{
                  width: 120,
                  height: 120,
                  borderRadius: "50%",
                  background: "#e9ecef",
                  border: "2px solid #fff",
                  boxShadow: "0 0 0 1px rgba(0,0,0,0.05)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 36,
                  fontWeight: 600,
                  color: "#6c757d",
                }}
                aria-label="Learner image placeholder"
              >
                {(pendingLearner?.user?.first_name?.[0] || "?").toUpperCase()}
              </div>
            </Col>
            <Col>
              <h4 className="mb-2">
                {`${pendingLearner?.user?.first_name ?? ""} ${pendingLearner?.user?.last_name ?? ""}`.trim()}
              </h4>
              <div className="mt-3">
                <Row>
                  {confirmFields.map((f) => (
                    <Col key={f.label} xs={12} className="mb-2">
                      <strong>{f.label}:</strong> {f.value}
                    </Col>
                  ))}
                </Row>
              </div>
            </Col>
          </Row>
        </Container>
      </CommonModal>

      <CommonModal
        show={showAlert.userNotFound}
        onHide={() => hideAlert("userNotFound")}
        title={<Modal.Title>Alert</Modal.Title>}
        footer={
          <Button variant="dark" onClick={() => hideAlert("userNotFound")} className="px-4">
            Ok
          </Button>
        }
        modalProps={{
          centered: true,
          backdrop: true,
          className: "manage-roster-modal",
        }}
      >
        User not found.
      </CommonModal>

      <ToastContainer />
    </>
  );
};

export default ManageRoster;
